import { supabase } from '../supabase';

export async function checkUsernameAvailability(username: string) {
  const { data: existingUser, error } = await supabase
    .from('users')
    .select('id')
    .eq('username', username)
    .maybeSingle();

  if (error) throw error;
  return !existingUser;
}

export async function signUp(
  email: string, 
  password: string, 
  username: string,
  userType: 'member' | 'professional' = 'member',
  referralCode?: string
) {
  // First check username availability
  const isUsernameAvailable = await checkUsernameAvailability(username);
  if (!isUsernameAvailable) {
    throw new Error('This username is already taken. Please choose another one.');
  }

  // Create auth user
  const { data: { user, session }, error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username,
        isNewUser: true,
        needsOnboarding: true  // Add flag to prevent immediate redirect
      },
      emailRedirectTo: `${window.location.origin}`,
    }
  });

  if (signUpError) {
    // Handle specific error cases
    if (signUpError.message.includes('Password')) {
      throw new Error('Password must be at least 6 characters long');
    }
    if (signUpError.message.includes('email')) {
      throw new Error('Please enter a valid email address');
    }
    throw signUpError;
  }

  if (!user) throw new Error('Signup failed. Please try again.');

  // Create user profile via secure server-side RPC function.
  // This handles: profile creation, referral code generation, referral processing,
  // and safely sets user_type/subscription_status (not exploitable from client).
  const { data: profileResult, error: profileError } = await supabase.rpc('create_user_profile', {
    p_username: username,
    p_referral_code_used: referralCode?.trim() || null
  });

  if (profileError) {
    throw new Error(`Failed to create user profile: ${profileError.message}`);
  }

  const referralApplied: boolean | null = profileResult?.referralApplied ?? null;

  // Send welcome email - don't wait for it
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/welcome-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`
    }
  }).catch(() => {
    // Non-critical — silently ignore
  });
  
  return { user, session, referralApplied };
}

export async function signIn(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    // Handle specific error cases
    if (error.message.includes('Invalid login credentials')) {
      throw new Error('Invalid email or password');
    }
    if (error.message.includes('Email not confirmed')) {
      throw new Error('Please confirm your email address before signing in');
    }
    throw error;
  }
}

export async function isProfessional(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Use the new unified subscription system
    const { isProfessional } = await import('../subscription');
    return await isProfessional(user.id);
  } catch (error) {
    console.error('Error checking professional status:', error);
    return false;
  }
}